import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Tooltip } from './Shared';

export const ESG_RISKS = [
  'AI displacement',
  'Energy cost volatility',
  'Talent shortages',
  'Climate disruptions',
  'Regulatory pressure',
  'Client ESG demands'
];

export const RISK_ISSUE_MAP = {
  'AI displacement': 'ai_disruption',
  'Energy cost volatility': 'energy_consumption',
  'Talent shortages': 'talent_retention',
  'Climate disruptions': 'climate_resilience',
  'Client ESG demands': 'client_requirements',
};

export function SectionD({ data, updateSection, handleESGTargetsChange, handleRiskSelection, resetRiskSelection, getRiskRank }) {
  const sd = data.section_d;
  const ratedHigh = (risk) => {
    const issueId = RISK_ISSUE_MAP[risk];
    if (!issueId) return false;
    const b = (data.section_b || {})[issueId];
    return !!b && Math.max(b.financial || 0, b.impact || 0) >= 4;
  };

  return (
    <div className="space-y-6" data-testid="section-d-content">
      <div>
        <Tooltip text="Indicate if your organization has established ESG targets. Data source: Sustainability strategy documents, board resolutions. If need more information, click the AI Assist button.">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            D1. Does your organisation have ESG targets?
          </label>
        </Tooltip>
        <div className="flex gap-3">
          {['Yes', 'No'].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleESGTargetsChange(opt === 'Yes')}
              className={`flex-1 h-12 rounded-xl border font-medium transition-all ${
                sd.has_esg_targets === (opt === 'Yes')
                  ? 'border-accent bg-accent/5 text-accent'
                  : 'border-slate-200 text-slate-600'
              }`}
              data-testid={`esg-targets-${opt.toLowerCase()}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className={sd.has_esg_targets === false ? 'opacity-50' : ''}>
        <Tooltip text="Your organization's target for reducing carbon emissions by 2030. Data source: Sustainability targets, climate commitments. This question is only applicable if you answered 'Yes' to D1. If need more information, click the AI Assist button.">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            D2. Target Carbon Reduction by 2030 (%)
          </label>
        </Tooltip>
        <input
          type="number"
          value={sd.has_esg_targets === false ? '' : (sd.carbon_target_2030 || '')}
          onChange={(e) => updateSection('section_d', 'carbon_target_2030', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
          disabled={sd.has_esg_targets === false}
          className={`w-full h-12 px-4 rounded-xl border border-slate-200 ${sd.has_esg_targets === false ? 'bg-slate-100 cursor-not-allowed' : ''}`}
          placeholder={sd.has_esg_targets === false ? 'N/A - No ESG targets' : 'e.g., 30'}
          data-testid="carbon-target-input"
        />
        {sd.has_esg_targets === false && (
          <p className="text-xs text-slate-500 mt-1">This field is disabled because you answered "No" to D1.</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Tooltip text="Select your top 3 ESG risks in order of priority. Click to select 1st, 2nd, and 3rd choices. Data source: Risk assessment, strategic planning documents. If need more information, click the AI Assist button.">
            <label className="block text-sm font-medium text-slate-700">
              D3. Top 3 ESG Risks in Next 3 Years (Select in order of priority)
            </label>
          </Tooltip>
          <button
            type="button"
            onClick={resetRiskSelection}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-all"
            data-testid="reset-risks-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Selected: {(sd.top_risks || []).length}/3 
          {(sd.top_risks || []).length > 0 && (
            <span> — {(sd.top_risks || []).map((r, i) => `${i+1}. ${r}`).join(', ')}</span>
          )}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {ESG_RISKS.map((risk) => {
            const rank = getRiskRank(risk);
            const isSelected = rank !== null;
            const canSelect = (sd.top_risks || []).length < 3 || isSelected;

            return (
              <button
                key={risk}
                type="button"
                onClick={() => canSelect && handleRiskSelection(risk)}
                disabled={!canSelect}
                className={`p-3 rounded-xl border text-sm font-medium transition-all relative ${
                  isSelected
                    ? 'border-accent bg-accent/5 text-accent'
                    : canSelect
                      ? 'border-slate-200 text-slate-600 hover:border-slate-300'
                      : 'border-slate-100 text-slate-400 cursor-not-allowed bg-slate-50'
                }`}
                data-testid={`risk-${risk.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {isSelected && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-accent text-primary text-xs font-bold rounded-full flex items-center justify-center">
                    {rank}
                  </span>
                )}
                {risk}
                {ratedHigh(risk) && (
                  <span
                    className="block text-[10px] text-accent-dark font-semibold mt-1"
                    data-testid={`risk-hint-${risk.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    ★ rated high in Section B
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
